import { useMemo } from 'react';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';

import { ImagePreview } from '../camera';

import { REQUIRED_ATTACHMENTS } from '~common';
import { PaddingBox } from '~components/atoms';
import { FieldInfo } from '~components/molecules';
import { useQueryGetReason } from '~hooks/reason/useQueryGetReason';
import { useVenuePlacementCreationContext } from '~providers';
import {
  CAMERA_STEP,
  CameraProvider,
  useCameraContext,
} from '~providers/CameraProvider';
import { UnpinningReasonKey } from '~types';
import { getPlacementUnpinningDetail } from '~utils/unpinning.util';

const reasonQueryParams = (key: UnpinningReasonKey) => ({
  page: 0,
  size: 20,
  filter: { filterByKeys: [key] },
});

type Props = {
  index: number;
};
type StationPhotoProps = {
  url: string;
};

const StationPhoto: React.FC<StationPhotoProps> = ({ url }) => {
  const { onOpenPreviewView, cameraStep } = useCameraContext();

  return (
    <Grid item xs={4}>
      <Paper
        sx={{
          border: '1px solid',
          borderColor: 'neutral.200',
          cursor: 'pointer',
          overflow: 'hidden',
          height: '150px', // Adjust height for larger images
        }}
        onClick={() => {
          onOpenPreviewView(url);
        }}
      >
        <Box
          sx={{
            backgroundImage: `url(${url})`,
            width: '100%',
            height: '100%',
            backgroundSize: 'cover', // Fill the container
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
      </Paper>
      <ImagePreview isVisible={cameraStep === CAMERA_STEP.PREVIEW} />
    </Grid>
  );
};

export const PlacementDetailReviewStep: React.FC<Props> = ({ index }) => {
  const { placementListInfo } = useVenuePlacementCreationContext();
  const placementInfo = useMemo(
    () => placementListInfo[index],
    [index, placementListInfo],
  );

  const { reasonId, note } = useMemo(
    () => getPlacementUnpinningDetail(placementInfo),
    [placementInfo],
  );
  const swapReasons = useQueryGetReason(
    reasonQueryParams(UnpinningReasonKey.UNPINNING_REASONS_FOR_STATION_SWAPS),
  );
  const relocationReasons = useQueryGetReason(
    reasonQueryParams(
      UnpinningReasonKey.UNPINNING_REASONS_FOR_STATION_RELOCATION,
    ),
  );

  const unpinningReason = useMemo(() => {
    if (!reasonId) return undefined;
    const allReasons = [
      ...(swapReasons.data?.data ?? []),
      ...(relocationReasons.data?.data ?? []),
    ];
    return allReasons.find((reason) => reason.id === reasonId)?.reason;
  }, [reasonId, swapReasons.data, relocationReasons.data]);

  const stationPhotos = useMemo(() => {
    return placementInfo.attachment;
  }, [placementInfo.attachment]);

  return (
    <>
      <PaddingBox
        flex={1}
        sx={{
          bgcolor: 'background.paper',
          width: '100%',
          height: '100%',
        }}
      >
        <Box display="flex" flexDirection="column" gap={4} width="100%">
          {/* Placement Info */}
          <Stack gap={1}>
            <Typography variant="h6" color="text.black">
              {placementInfo.name}
            </Typography>
            <FieldInfo
              label="Station Type"
              value={placementInfo.station?.type || '---'}
            />
            <FieldInfo
              label="IMEI"
              value={placementInfo.station?.imei || '---'}
            />
            <FieldInfo
              label="Serial #"
              value={placementInfo.station?.serialNumber || '---'}
            />
            <FieldInfo
              label="Placement Type"
              value={placementInfo.placementType?.name || '---'}
            />
            <FieldInfo
              label="Dist. Channel"
              value={placementInfo.distributionChannel?.name || '---'}
            />
          </Stack>

          {/* Unpinning reason and note, only on a swap or a relocation */}
          {(unpinningReason || note) && (
            <Stack gap={1}>
              <Typography variant="h6" color="text.black">
                Unpinning
              </Typography>
              {unpinningReason && (
                <FieldInfo label="Reason" value={unpinningReason} />
              )}
              {note && <FieldInfo label="Note" value={note} />}
            </Stack>
          )}

          {/* Placement Location */}
          <Stack gap={1}>
            <Typography variant="h6" color="text.black">
              Coordinates
            </Typography>
            <FieldInfo
              label="Latitude"
              value={
                placementInfo.location?.latitude.toFixed(5).toString() || '---'
              }
            />
            <FieldInfo
              label="Longitude"
              value={
                placementInfo.location?.longitude.toFixed(5).toString() || '---'
              }
            />
          </Stack>

          {/* Station Photos */}
          <Stack gap={1}>
            <Typography variant="h6" color="text.black">
              Station Photos
            </Typography>
            <Grid container alignItems={'flex-start'} spacing={2}>
              {stationPhotos &&
                REQUIRED_ATTACHMENTS.map((attachment) => {
                  if (
                    stationPhotos[
                      attachment.key as keyof typeof stationPhotos
                    ] === ''
                  )
                    return null;

                  return (
                    <CameraProvider
                      key={attachment.key}
                      caption={attachment.caption}
                      description={attachment.description}
                    >
                      <StationPhoto
                        url={
                          stationPhotos[
                            attachment.key as keyof typeof stationPhotos
                          ] as string
                        }
                      />
                    </CameraProvider>
                  );
                })}
            </Grid>
          </Stack>
        </Box>
      </PaddingBox>
    </>
  );
};
